import React, { useContext, useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { AppContext } from "../context/EditSessionsContext";
import { useGetLink } from "../hooks/useGetLinks";
import { useGetBlocks } from "../hooks/useGetBlocks";
import { useLoadBlockingThreshold } from "../hooks/useLoadBlockingThreshold";
import { useOpenedProposal } from "../hooks/useOpenedProposal";
import EditSessionSkeleton from "./edit-session-skeleton";
import InitEditSessions from "./InitEditSessions";
import ProposalLoadError from "../components/proposal-load-error";
import { Types } from "../context/switchPage.reducer";
import { Types as TypeLink } from "../context/linkData.reducer";
import { Types as TypeResult } from "../context/result.reducer";
import { Types as TypeForm } from "../context/formData.reducer";
import useContextUrl from "@common/hooks/useContextUrl";
import { resetEditorProposal, setEditorReturnTo } from "@context/useAssignmentProposalStore";
import { useLoadApprovalFlowAbilities } from "@services/collections/assignment-proposal/assignmentProposalServices";
import type { ProposalEditorLocationState } from "@services/types/assignment-proposal/assignmentProposalTypes";
import { getProposalIdFromSearch, isReviewSearch } from "@utils/assignmentProposal";
import { LINK_SESSIONS_RELOAD_EVENT } from "../utils/linkSessionsReload";

const EditSessionLayout: React.FC = () => {
  const { state, dispatch } = useContext(AppContext);
  const [dataLinks, getDataLinks] = useGetLink();
  const [dataBlocks, getDataBlocks] = useGetBlocks();
  const { loadBlockingThreshold } = useLoadBlockingThreshold();
  const { id: linkId } = useContextUrl();
  const { search, state: locationState } = useLocation<ProposalEditorLocationState | undefined>();
  // Read on the first render: the form wipes the router state once it has applied what the URL
  // asked for, and the editor still needs the page to go back to after a decision or "Finalizar".
  const [returnTo] = useState(() => locationState?.returnTo ?? null);

  // Loaded once for the whole editor; skipped when the approval flow is off.
  useLoadApprovalFlowAbilities();

  useEffect(() => {
    setEditorReturnTo(returnTo);

    return () => setEditorReturnTo(null);
  }, [returnTo]);

  // The proposal of the editor belongs to one link and one visit. Clear only that: wiping the
  // whole store would also drop the loaded abilities. Declared before useOpenedProposal so a new
  // visit starts clean before the proposal of the URL is requested.
  useEffect(() => {
    resetEditorProposal();

    return () => resetEditorProposal();
  }, [linkId]);

  // The editor opened for an existing proposal (?proposalId=): loaded and applied over the link.
  const openedProposal = useOpenedProposal(getProposalIdFromSearch(search), isReviewSearch(search));

  const handleCleanData = () => {
    dispatch({ type: Types.CleanSwitchPage });
    dispatch({ type: TypeLink.CleanLinkData });
    dispatch({ type: TypeResult.CleanResultData });
    dispatch({ type: TypeForm.CleanForm, payload: true });
  };

  const handleGetInitialData = () => {
    handleCleanData();
    getDataLinks(true);
    getDataBlocks(true);
  };

  useEffect(() => {
    if (dataLinks === null) getDataLinks();
    if (dataBlocks.dataEnd === null) getDataBlocks();
    loadBlockingThreshold();

    window.addEventListener(LINK_SESSIONS_RELOAD_EVENT, handleGetInitialData);

    return () => {
      window.removeEventListener(LINK_SESSIONS_RELOAD_EVENT, handleGetInitialData);
      handleCleanData();
    };
  }, []);

  // Never the form with a failed proposal: an empty editor would pass for a new proposal.
  if (openedProposal.status === "error") {
    return (
      <ProposalLoadError errorCode={openedProposal.errorCode} onRetry={openedProposal.retry} />
    );
  }

  if (
    !dataLinks ||
    !dataBlocks ||
    !state?.link?.info ||
    // The proposal must be applied before the form renders for the first time.
    openedProposal.status === "loading"
  ) {
    return <EditSessionSkeleton />;
  }

  return <InitEditSessions />;
};

export default EditSessionLayout;
